/**
 * App Usage Tracking Composable
 *
 * Provides functionality to track user activity in the App Usage API.
 * Supports both single event recording and batch processing.
 *
 * Usage:
 *   import { useAppUsage } from '@/composables/useAppUsage'
 *
 *   const { setUser, recordEvent, recordBatch, startBatchTimer } = useAppUsage()
 *
 *   // Set the current user
 *   setUser('user123')
 *
 *   // Record a single event (fire-and-forget)
 *   recordEvent('page:Sales', 'view')
 *
 *   // Start auto-flush timer (every 30 seconds)
 *   startBatchTimer()
 */

import axios from 'axios'

// Configuration - use window.synergyApi if available (set in GlobalVariables.js)
const getApiBaseUrl = () => {
  const apiBase = (typeof window !== 'undefined' && window.synergyApi)
    ? window.synergyApi
    : 'https://lna.pos.co.zm/SynergyApi/api/v1/'

  return apiBase + 'usage'
}

// Get app_name for usage tracking API
// Must match registered app in SynergyApi
const getAppKey = () => {
  return 'lytsoft-backoffice'
}

// Module-level state (shared across all composable instances)
const eventQueue = []
let batchTimer = null
const BATCH_INTERVAL = 30000 // 30 seconds

// Current user ID (set externally via setUser)
let currentUserId = null

/**
 * Set the current user for tracking
 * @param {string|number} userId - The user ID
 */
export function setUser(userId) {
  currentUserId = userId ? String(userId) : null
}

/**
 * Get the current user ID
 * @returns {string|null} The current user ID
 */
export function getUser() {
  return currentUserId
}

/**
 * Record a single usage event (fire-and-forget)
 * @param {string} feature - The feature being accessed (e.g., 'page:Sales', 'action:Cashup')
 * @param {string} action - The action type (default: 'view')
 */
export function recordEvent(feature, action = 'view') {
  const appKey = getAppKey()
  if (!appKey) return // Skip if no app_key configured

  const event = {
    app_name: appKey,
    feature: feature,
    action: action,
    user_id: currentUserId
  }

  // Fire-and-forget - don't await, don't block UI
  axios.post(`${getApiBaseUrl()}/record`, event)
    .catch(() => {
      // Silently ignore errors - analytics should not affect user experience
    })
}

/**
 * Record an event to the batch queue
 * Use recordBatch() to flush the queue
 * @param {string} feature - The feature being accessed
 * @param {string} action - The action type (default: 'view')
 */
export function queueEvent(feature, action = 'view') {
  const appKey = getAppKey()
  if (!appKey) return // Skip if no app_key configured

  const event = {
    app_name: appKey,
    feature: feature,
    action: action,
    user_id: currentUserId
  }

  eventQueue.push(event)
}

/**
 * Flush the event queue (send all queued events as a batch)
 */
export async function recordBatch() {
  if (eventQueue.length === 0) {
    return
  }

  // Copy and clear the queue
  const events = [...eventQueue]
  eventQueue.length = 0

  // Fire-and-forget batch request
  axios.post(`${getApiBaseUrl()}/record/batch`, events)
    .catch(() => {
      // Silently ignore errors
    })
}

/**
 * Start the batch timer (auto-flush every 30 seconds)
 * @param {number} intervalMs - Custom interval in milliseconds (default: 30000)
 */
export function startBatchTimer(intervalMs = BATCH_INTERVAL) {
  // Clear existing timer if any
  stopBatchTimer()

  // Set up new timer
  batchTimer = setInterval(() => {
    recordBatch()
  }, intervalMs)
}

/**
 * Stop the batch timer
 */
export function stopBatchTimer() {
  if (batchTimer) {
    clearInterval(batchTimer)
    batchTimer = null
  }
}

/**
 * Get the current queue size
 * @returns {number} Number of events in queue
 */
export function getQueueSize() {
  return eventQueue.length
}

/**
 * Main composable function that provides all tracking functionality
 * @returns {Object} The tracking API
 */
export function useAppUsage() {
  return {
    setUser,
    getUser,
    recordEvent,
    queueEvent,
    recordBatch,
    startBatchTimer,
    stopBatchTimer,
    getQueueSize,
    // Expose for external use
    getAppKey,
    getApiBaseUrl
  }
}

export default useAppUsage
